/**
 * A ÁRVORE DOS DOCUMENTOS DE HABILITAÇÃO (14/10/2026, seletor da publicação, PR-1).
 *
 * A mesma árvore que Licitações → Documentos desenha (LicitacoesTab, a pasta): categoria → [grupo] → subcategoria →
 * documento. O GRUPO nasce quando duas ou mais subcategorias da mesma categoria compartilham o prefixo antes de "/"
 * ("Equipamentos/Ramais" e "Equipamentos/Gateways" viram o grupo "Equipamentos"); com uma só, fica plana. Aqui como
 * função pura para o seletor da publicação usar e testar; a tela de Documentos ainda tem a sua cópia (adotar esta é
 * mudança da LicitacoesTab, fora desta PR).
 */
import { getDaysUntil } from './formatters';

export const CATEGORIAS_DE_DOCUMENTO: Record<string, string> = {
  habilitacao_fiscal: 'Habilitação Fiscal',
  habilitacao_juridica: 'Habilitação Jurídica',
  qualificacao_economica: 'Qualificação Econômico-Financeira',
  qualificacao_tecnica: 'Qualificação Técnica',
  declaracoes: 'Declarações e Propostas',
  sicaf: 'SICAF',
  equipamentos: 'Equipamentos',
  _sem_categoria: 'Sem categoria',
};
export const ORDEM_DAS_CATEGORIAS = Object.keys(CATEGORIAS_DE_DOCUMENTO);

export interface Subpasta { chave: string; nome: string; docs: any[] }
export interface GrupoDeSubpastas { chave: string; nome: string; subpastas: Subpasta[] }
export interface Pasta { chave: string; nome: string; soltos: any[]; itens: (({ tipo: 'sub' } & Subpasta) | ({ tipo: 'grupo' } & GrupoDeSubpastas))[] }

const porNome = (a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || ''), 'pt-BR');

export function montarArvore(docs: any[]): Pasta[] {
  const porCategoria: Record<string, any[]> = {};
  for (const d of docs) {
    const c = d?.category && CATEGORIAS_DE_DOCUMENTO[d.category] ? d.category : (d?.category || '_sem_categoria');
    (porCategoria[c] = porCategoria[c] || []).push(d);
  }
  const ordem = [...ORDEM_DAS_CATEGORIAS.filter((c) => porCategoria[c]), ...Object.keys(porCategoria).filter((c) => !ORDEM_DAS_CATEGORIAS.includes(c)).sort()];
  return ordem.map((cat) => {
    const lista = porCategoria[cat];
    const soltos = lista.filter((d) => !d.subcategory).sort(porNome);
    const subs: Record<string, any[]> = {};
    for (const d of lista.filter((x) => x.subcategory)) (subs[d.subcategory] = subs[d.subcategory] || []).push(d);
    const chavesSub = Object.keys(subs).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    const prefixos: Record<string, string[]> = {};
    for (const s of chavesSub) {
      const i = s.indexOf('/');
      if (i > 0) (prefixos[s.slice(0, i)] = prefixos[s.slice(0, i)] || []).push(s);
    }
    const agrupados = new Set(Object.keys(prefixos).filter((p) => prefixos[p].length >= 2));
    const feitos = new Set<string>();
    const itens: Pasta['itens'] = [];
    for (const s of chavesSub) {
      const i = s.indexOf('/');
      const p = i > 0 ? s.slice(0, i) : '';
      if (p && agrupados.has(p)) {
        if (feitos.has(p)) continue;
        feitos.add(p);
        itens.push({
          tipo: 'grupo', chave: `${cat}:${p}`, nome: p,
          subpastas: prefixos[p].map((sk) => ({ chave: `${cat}:${sk}`, nome: sk.slice(p.length + 1), docs: subs[sk].sort(porNome) })),
        });
      } else {
        itens.push({ tipo: 'sub', chave: `${cat}:${s}`, nome: s, docs: subs[s].sort(porNome) });
      }
    }
    return { chave: cat, nome: CATEGORIAS_DE_DOCUMENTO[cat] || cat, soltos, itens };
  });
}

/** Todos os ids de documento debaixo de um nó (pasta, grupo ou subpasta). */
export function idsDe(no: Pasta | GrupoDeSubpastas | Subpasta | ({ tipo: 'sub' } & Subpasta) | ({ tipo: 'grupo' } & GrupoDeSubpastas)): number[] {
  if ('soltos' in no) return [...no.soltos.map((d) => d.id), ...no.itens.flatMap((i) => idsDe(i))];
  if ('subpastas' in no) return no.subpastas.flatMap((s) => idsDe(s));
  return no.docs.map((d) => d.id);
}

/** O checkbox de uma pasta: todos marcados, alguns (indeterminado) ou nenhum. */
export function estadoDaMarcacao(ids: number[], marcados: Set<number>): 'todos' | 'alguns' | 'nenhum' {
  const n = ids.filter((id) => marcados.has(id)).length;
  return n === 0 ? 'nenhum' : n === ids.length ? 'todos' : 'alguns';
}

/** Marcar a pasta marca os filhos; se já estavam todos, desmarca todos. */
export function alternarPasta(ids: number[], marcados: Set<number>): Set<number> {
  const prox = new Set(marcados);
  if (estadoDaMarcacao(ids, marcados) === 'todos') ids.forEach((id) => prox.delete(id));
  else ids.forEach((id) => prox.add(id));
  return prox;
}

/**
 * A validade de um documento para quem vai mandar o link ao órgão: vencido; vencendo (30 dias ou menos, a regra dos
 * selos de prazo); ou sem problema. `noExpiry` = não vence (contrato social).
 */
export function validadeDoDocumento(d: any): 'vencido' | 'vencendo' | 'ok' {
  if (d?.noExpiry || !d?.expiryDate) return 'ok';
  const dias = getDaysUntil(d.expiryDate);
  if (dias == null) return 'ok';
  if (dias < 0) return 'vencido';
  return dias <= 30 ? 'vencendo' : 'ok';
}
